import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Does blogging really drive sales?",
  "description": "",
  "published": "2018-04-17T00:00:00.000Z",
  "redirect_from": ["/blog/blogging-drive-sales/swizec/8338"],
  "categories": "Learning, Personal, Side Projects, Thoughts",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Wanna see a scary graph? Here's a scary graph.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/2018-book-sales.png",
        "alt": "Gumroad book sales for React+D3 Jan to Apr 2018"
      }}></img></p>
    <p>{`Those are book sales on Gumroad. Grey bars are views, and the green line is sales volume in dollars.`}</p>
    <p>{`Can you spot the problem?`}</p>
    <p>{`Here's the same graph for 4 months before New Year's with an outlier removed so you can see the trend. The 30-at-30 promotion I ran for my birthday makes the rest look tiny.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/2017-book-sales.png",
        "alt": "Gumroad book sales for React+D3 Sep to Dec 2017"
      }}></img></p>
    <p>{`About twice as much revenue. A little more than twice as much in fact, but there're two more weeks of April.`}</p>
    <p>{`The beginning of September still feels the effects of a launch. Sales are high. Then it starts tapering off and sales become less and less frequent.`}</p>
    <p>{`This trend continues all the way until the beginning of April where sales frequency starts picking up again.`}</p>
    <p>{`🤔`}</p>
    <p>{`Clearly my `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React+D3`}</a>{` book is due for another launch. Otherwise, it's going to slip beyond the point of no return, and nobody wants that. It's a good book, and it deserves to live.`}</p>
    <p>{`Let's correlate those sales with blog traffic.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/traffic-screenshot.png",
        "alt": "swizec.com traffic Sep 2017 to Apr 2018"
      }}></img></p>
    <p>{`Stable traffic. Not bad, not spectacular. The lack of growth is worrying, but there's a lot of things I could do about that. Use the mailing list to drive traffic instead of audience relationship, do more to re-share old content, run ads, better SEO tricks, etc.`}</p>
    <p>{`When you switch to a monthly view, you see a slight downward trend. `}<em parentName="p">{`However`}</em>{`, over the past 3 years, traffic follows what could be the slow ramp up towards an exponential curve.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/3-year-screenshot.png",
        "alt": "Blog traffic from Apr 2015 to Apr 2018"
      }}></img></p>
    <p>{`Traffic went from 5,000 pageviews per week in April 2015 to 20,000 in April 2018. Not bad for a bloke writing in his spare time.`}</p>
    <p>{`Sales follow a similar pattern. Gumroad won't let me switch to a per-week view, but you can see density increase.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/gumroad-3-year-sales.png",
        "alt": "Gumroad book sales Apr 2015 to Apr 2018"
      }}></img></p>
    <p>{`Okay, so the relation between blog traffic and Gumroad sales volume is there. Not a super strong correlation, but it seems related nonetheless.`}</p>
    <p>{`What about blogging volume?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/blogs-per-week-2017.png",
        "alt": "Publishing volume per week 2017"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/blogs-per-week-2018.png",
        "alt": "Publishing volume per week 2018"
      }}></img></p>
    <p>{`🤔`}</p>
    <p>{`It seems despite my valiant efforts, publishing volume has been pretty constant at 2 to 3 articles per week.`}</p>
    <p>{`Publishing volume, therefore, doesn't have a direct impact on sales either. It might have a long-term impact in that the more I publish, the more chances there are Google will recommend my blog as an answer to your question.`}</p>
    <p>{`That must be where the growing traffic is coming from. Because it sure doesn't seem to be coming from each individual article when I first publish.`}</p>
    <p>{`Outside spikes when I get lucky, of course.`}</p>
    <p>{`Now here's a graph that correlates with sales very well. Book landing page traffic.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/landing-page-traffic.png",
        "alt": "React+D3 landing page traffic Apr 2015 to Apr 2018"
      }}></img></p>
    <p>{`When traffic goes up, Gumroad sales density increases. 🧐`}</p>
    <p>{`Now we're onto something. The more people that come to my landing page, the more sales I make.`}</p>
    <p>{`But if not from my blog, where are those people coming from?`}</p>
    <p>{`To be honest, I don't know. Google says 40% come from "Direct" and another 40% come from "Referral" and "Organic Search"`}</p>
    <p>{`And the biggest referral? `}<a parentName="p" {...{
        "href": "https://es6cheatsheet.com/"
      }}>{`es6cheatsheet.com`}</a>{`, which doesn't even link to `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React+D3`}</a>{`.`}</p>
    <p>{`¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`There is a lesson in all this data. I think it's that I should launch more useful side projects and either spend less time blogging or more time optimizing and promoting each post and blog as a whole.`}</p>
    <p>{`That said, more than 30% of my sales come from Email, and most email subscribers come from the blog. 🤯`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/strategic-plan.jpg",
        "alt": null
      }}></img></p>
    <p>{`Guess for the foreseeable future the strategy remains Doing Things.`}</p>
    <p>{`Or maybe I just have no idea what I'm doing at all ...`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      